import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Button,
	Divider,
	Group,
	Modal,
	Select,
	SegmentedControl,
	Stack,
	Text,
	TextInput,
} from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type {
	AddCounterpartyFormValues,
	AssignableUser,
	SlotSubmission,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.types';
import {
	emptySlot,
	toSlotSubmissions,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.utils';
import type { AddCounterpartyPlacementRes } from '@dealstack/shared/lib/backendClient';
import { toFixedPlacements } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyPlacement.utils';
import { CounterpartyPlacementStep } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/CounterpartyPlacementStep';
import { useCounterpartyPlacement } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement';
import type { CounterpartyPlacementPayload } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement.types';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	dealId: string;
	maxSlots: number;
	freeRows: number | null;
	totalRows: number | null;
	/** Non-null when the document stamps by hand, which adds the placement step. */
	placementRequirement: AddCounterpartyPlacementRes | null;
	assignableUsers: AssignableUser[];
	isSubmitting: boolean;
	onSubmit: (values: {
		name: string;
		slots: SlotSubmission[];
		placements: CounterpartyPlacementPayload[];
	}) => Promise<void>;
}

export const AddCounterpartyModal = ({
	isOpen,
	onClose,
	dealId,
	maxSlots,
	freeRows,
	totalRows,
	placementRequirement,
	assignableUsers,
	isSubmitting,
	onSubmit,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [isPlacing, setIsPlacing] = useState(false);

	const schema = useMemo(
		() =>
			z
				.object({
					name: z
						.string()
						.trim()
						.min(1, tMipManage('documents_page.add_counterparty.modal.name_required')),
					slotCount: z.number().int().min(1).max(maxSlots),
					slots: z.array(
						z.object({
							mode: z.enum(['UNASSIGNED', 'USER', 'INVITE']),
							userId: z.string(),
							firstName: z.string(),
							lastName: z.string(),
							email: z.string(),
						})
					),
				})
				// Cross-field: only the slots actually rendered have to be complete, and only for
				// the branch the manager picked.
				.superRefine((values, ctx) => {
					const seenUserIds = new Set<string>();
					const seenEmails = new Set<string>();
					values.slots.slice(0, values.slotCount).forEach((slot, index) => {
						if (slot.mode === 'USER' && !slot.userId) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['slots', index, 'userId'],
								message: tMipManage(
									'documents_page.add_counterparty.modal.user_required'
								),
							});
						}
						// One person cannot fill two slots of the same party — two signatories
						// means two people. The server refuses it either way; flagging it here
						// keeps the manager out of a 400.
						if (slot.mode === 'USER' && slot.userId) {
							if (seenUserIds.has(slot.userId)) {
								ctx.addIssue({
									code: z.ZodIssueCode.custom,
									path: ['slots', index, 'userId'],
									message: tMipManage(
										'documents_page.add_counterparty.modal.user_duplicate'
									),
								});
							}
							seenUserIds.add(slot.userId);
						}
						if (slot.mode !== 'INVITE') {
							return;
						}
						if (!slot.firstName.trim()) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['slots', index, 'firstName'],
								message: tMipManage(
									'documents_page.add_counterparty.modal.first_name_required'
								),
							});
						}
						if (!slot.lastName.trim()) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['slots', index, 'lastName'],
								message: tMipManage(
									'documents_page.add_counterparty.modal.last_name_required'
								),
							});
						}
						const email = slot.email.trim();
						if (!z.string().email().safeParse(email).success) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['slots', index, 'email'],
								message: tMipManage(
									'documents_page.add_counterparty.modal.email_invalid'
								),
							});
							return;
						}
						const normalisedEmail = email.toLowerCase();
						if (seenEmails.has(normalisedEmail)) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['slots', index, 'email'],
								message: tMipManage(
									'documents_page.add_counterparty.modal.email_duplicate'
								),
							});
						}
						seenEmails.add(normalisedEmail);
					});
				}),
		[tMipManage, maxSlots]
	);

	const form = useForm<AddCounterpartyFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: '',
			slotCount: 1,
			// Fixed length, so changing the slot count is a render decision rather than a field-array
			// mutation — no effect needed to keep the array in step with the counter.
			slots: [emptySlot(), emptySlot()],
		},
	});

	const slotCount = form.watch('slotCount');
	const slotModes = form.watch('slots').map((slot) => slot.mode);
	const partyName = form.watch('name');

	// Held at modal level so the boxes survive stepping back to the details, and derived from the
	// live name/count so editing either re-labels and re-counts the slot panel with no effect.
	const fixedPlacements = useMemo(
		() => toFixedPlacements(placementRequirement?.occupiedBoxes ?? []),
		[placementRequirement]
	);
	const placement = useCounterpartyPlacement({
		partyName,
		slotCount,
		showStampMetadata: placementRequirement?.showStampMetadata ?? true,
		fixedPlacements,
	});

	// `placement.reset()` belongs with every `form.reset()`: this component stays mounted between
	// opens, so a box left behind would be submitted as the next counterparty's.
	const handleClose = () => {
		form.reset();
		placement.reset();
		setIsPlacing(false);
		onClose();
	};

	const handleSubmit = form.handleSubmit(async (values) => {
		await onSubmit({
			name: values.name.trim(),
			slots: toSlotSubmissions(values),
			placements: placementRequirement ? placement.placements : [],
		});
		form.reset();
		placement.reset();
		setIsPlacing(false);
	});

	// Details must be valid before the canvas opens: the slot panel is labelled from the party
	// name and sized by the slot count, so drawing against an unvalidated pair wastes the work.
	const handleNext = async () => {
		if (await form.trigger()) {
			setIsPlacing(true);
		}
	};

	const slotCountOptions = Array.from({ length: maxSlots }, (_, index) => ({
		value: String(index + 1),
		label: String(index + 1),
	}));

	return (
		<Modal.Root
			opened={isOpen}
			onClose={handleClose}
			centered
			size={isPlacing ? '90rem' : 'md'}
		>
			<Modal.Overlay />
			{/* data-testid goes on Content, not Root: Root has no box of its own, so a Playwright
			    visibility check against it never resolves even while the modal is genuinely open. */}
			<Modal.Content data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL}>
				<Modal.Header>
					<Modal.Title>
						{tMipManage('documents_page.add_counterparty.modal.title')}
					</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>
					{isPlacing && placementRequirement ? (
						<div data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_PLACEMENT_STEP}>
							<CounterpartyPlacementStep
								dealId={dealId}
								fileId={placementRequirement.fileId}
								showStampMetadata={placementRequirement.showStampMetadata}
								fixedPlacements={fixedPlacements}
								placement={placement}
								footer={
									<>
										<Button
											disabled={
												isSubmitting ||
												!placement.isComplete ||
												placement.hasOverlappingPlacements
											}
											loading={isSubmitting}
											onClick={() => void handleSubmit()}
											data-testid={
												TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_SUBMIT_BUTTON
											}
										>
											{tMipManage(
												'documents_page.add_counterparty.modal.submit'
											)}
										</Button>
										<Button
											variant="default"
											onClick={() => setIsPlacing(false)}
											disabled={isSubmitting}
											data-testid={
												TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_BACK_BUTTON
											}
										>
											{tMipManage(
												'documents_page.add_counterparty.modal.back'
											)}
										</Button>
									</>
								}
							/>
						</div>
					) : (
						<Stack gap="sm">
							{freeRows !== null && totalRows !== null && (
								<Text
									size="sm"
									c="dimmed"
									data-testid={
										TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_SLOTS_COUNTER
									}
								>
									{tMipManage(
										'documents_page.add_counterparty.modal.slots_free',
										{
											free: freeRows,
											total: totalRows,
										}
									)}
								</Text>
							)}

							<Controller
								control={form.control}
								name="name"
								render={({ field, fieldState }) => (
									<TextInput
										{...field}
										label={tMipManage(
											'documents_page.add_counterparty.modal.name_label'
										)}
										error={fieldState.error?.message}
										data-testid={
											TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_NAME_INPUT
										}
									/>
								)}
							/>

							<Controller
								control={form.control}
								name="slotCount"
								render={({ field }) => (
									<Select
										label={tMipManage(
											'documents_page.add_counterparty.modal.slot_count_label'
										)}
										data={slotCountOptions}
										value={String(field.value)}
										onChange={(value) => field.onChange(Number(value ?? 1))}
										allowDeselect={false}
										data-testid={
											TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_SLOT_COUNT
										}
									/>
								)}
							/>

							{Array.from({ length: slotCount }, (_, index) => index).map((index) => (
								<Stack
									key={index}
									gap="xs"
									data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_SLOT}
								>
									<Divider />
									<Text size="sm" fw={500}>
										{tMipManage(
											'documents_page.add_counterparty.modal.slot_label',
											{
												index: index + 1,
											}
										)}
									</Text>

									<Controller
										control={form.control}
										name={`slots.${index}.mode`}
										render={({ field }) => (
											<SegmentedControl
												fullWidth
												value={field.value}
												onChange={field.onChange}
												data={[
													{
														value: 'UNASSIGNED',
														label: tMipManage(
															'documents_page.add_counterparty.modal.mode_unassigned'
														),
													},
													{
														value: 'USER',
														label: tMipManage(
															'documents_page.add_counterparty.modal.mode_user'
														),
													},
													{
														value: 'INVITE',
														label: tMipManage(
															'documents_page.add_counterparty.modal.mode_invite'
														),
													},
												]}
											/>
										)}
									/>

									{slotModes[index] === 'USER' && (
										<Controller
											control={form.control}
											name={`slots.${index}.userId`}
											render={({ field, fieldState }) => (
												<Select
													label={tMipManage(
														'documents_page.add_counterparty.modal.user_label'
													)}
													searchable
													data={assignableUsers.map((user) => ({
														value: user.id,
														label: user.label,
													}))}
													value={field.value || null}
													onChange={(value) =>
														field.onChange(value ?? '')
													}
													error={fieldState.error?.message}
												/>
											)}
										/>
									)}

									{slotModes[index] === 'INVITE' && (
										<Group grow align="flex-start">
											<Controller
												control={form.control}
												name={`slots.${index}.firstName`}
												render={({ field, fieldState }) => (
													<TextInput
														{...field}
														label={tMipManage(
															'documents_page.add_counterparty.modal.first_name_label'
														)}
														error={fieldState.error?.message}
													/>
												)}
											/>
											<Controller
												control={form.control}
												name={`slots.${index}.lastName`}
												render={({ field, fieldState }) => (
													<TextInput
														{...field}
														label={tMipManage(
															'documents_page.add_counterparty.modal.last_name_label'
														)}
														error={fieldState.error?.message}
													/>
												)}
											/>
										</Group>
									)}

									{slotModes[index] === 'INVITE' && (
										<Controller
											control={form.control}
											name={`slots.${index}.email`}
											render={({ field, fieldState }) => (
												<TextInput
													{...field}
													label={tMipManage(
														'documents_page.add_counterparty.modal.email_label'
													)}
													error={fieldState.error?.message}
												/>
											)}
										/>
									)}
								</Stack>
							))}

							<Group justify="right" gap="xs">
								<Button
									variant="default"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{tMipManage('documents_page.add_counterparty.modal.cancel')}
								</Button>
								{placementRequirement ? (
									<Button
										onClick={() => void handleNext()}
										disabled={isSubmitting}
										data-testid={
											TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_NEXT_BUTTON
										}
									>
										{tMipManage('documents_page.add_counterparty.modal.next')}
									</Button>
								) : (
									<Button
										disabled={isSubmitting}
										loading={isSubmitting}
										onClick={() => void handleSubmit()}
										data-testid={
											TEST_ELEMENT_IDS.ADD_COUNTERPARTY_MODAL_SUBMIT_BUTTON
										}
									>
										{tMipManage('documents_page.add_counterparty.modal.submit')}
									</Button>
								)}
							</Group>
						</Stack>
					)}
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
